var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Clock, Color, Raycaster, Scene, Vector2, Vector3, Vector4, WebGLRenderer } from 'three';
import { Planogram } from './planogram';
import LodProvider from 'shared/lod/LodProvider';
import { MAX_ITEM_RENDER_ORDER, SphereItem } from './sphere_item';
import { LodSource } from 'shared/lod/interfaces';
import { isLodItem } from './utils/planogram_utils';
import PlanogramPoint from 'shared/utils/PlanogramPoint';
import loadingProgress, { LOADING_STAGES } from './api/services/loading_progress.service';
import { backgroundDataToLodItem, createLodId, parseLodId, skipAtlasLevels } from 'shared/lod/helpers';
import { disposeObject3D } from './utils/disposeThree';
import { SphereItemType } from 'shared/interfaces/planogram';
import { BrowserUtils } from './utils/browser_utils';
import { debugFloatPrameter, debugLog } from 'shared/utils/debug';
import cameraConfig from 'shared/config/CameraConfig';
import { packShading } from 'shared/utils/format';
import { resolvePaletteColor } from 'shared/utils/colors';
import { sphereRenderingEngine } from 'shared/renderingEngine/SphereRenderingEngine';
import { shiftObjectsCloserToCamera } from 'shared/utils/CameraUtils';
import { groupOrder, makeOverflowMeshes } from 'shared/utils/three-utils';
import { SphereShape } from 'shared/utils/SphereShape';
import { ViewableLimits } from 'shared/renderingEngine/ViewableLimits';
const MAX_LOD_LOADING_DURATION = debugFloatPrameter('MAX_LOD_LOADING_DURATION', 1700);
const BASE_BLUR_SIZE = 29; // hand picked to match previous blur implementation
// using full device pixel ratio can put the renderer resolution over the max allowed texture size on some devices, which crashes post-processing
function limitDevicePixelRatio(renderer) {
    const maxTextureSize = renderer.capabilities.maxTextureSize;
    const size = renderer.getSize(new Vector2());
    return Math.min(window.devicePixelRatio, maxTextureSize / size.x, maxTextureSize / size.y);
}
function getItemId(item) {
    return item.id;
}
function getLodId(item) {
    var _a, _b;
    const pictureData = item.data;
    const id = (_b = (_a = pictureData.picture) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : pictureData.id;
    if (pictureData.isFigma) {
        return createLodId(id, LodSource.FIGMA);
    }
    else {
        return createLodId(id, LodSource.DEFAULT);
    }
}
function pickDeviceLimits() {
    if (BrowserUtils.detectWebkitInAppBrowser() && BrowserUtils.iPhoneMachineId() < 12.1) {
        return {
            physicalTextureCount: 3,
            physicalTextureResolution: 2048
        };
    }
    else if (BrowserUtils.isMobileSafari()) {
        return {
            physicalTextureCount: 4
        };
    }
    else {
        return {
            physicalTextureCount: 7
        };
    }
}
function measureCanvasSize() {
    // TODO: set canvas size with styles instead, measure renderer.domElement's size here
    return new Vector2(window.innerWidth, window.innerHeight);
}
export default class CanvasRenderer {
    get capabilities() {
        return this.renderer.capabilities;
    }
    get domElement() {
        return this.renderer.domElement;
    }
    constructor(canvas, planogram) {
        this.canvas = canvas;
        this.planogram = planogram;
        this.lodPaused = false;
        this.clock = new Clock();
        this.items = [];
        this.viewportListeners = [];
        this.renderListeners = [];
        this.sphereShape = new SphereShape(Planogram.ALPHA, 3, this.planogram.width);
        {
            this.scene = new Scene();
            this.scene.background = new Color(resolvePaletteColor(planogram.backgroundColor, planogram.palette));
        }
        this.renderer = new WebGLRenderer({
            canvas,
            alpha: true
        });
        {
            this.viewableLimits = new ViewableLimits(planogram.topLimit, planogram.bottomLimit);
            const startPoint = (this.planogram.startPoint / this.planogram.width) * 360.0;
            sphereRenderingEngine.init(this.scene, measureCanvasSize(), planogram.cameraPosition, 1, startPoint);
            this.cameraControls = sphereRenderingEngine.cameraControls;
            this.camera = this.cameraControls.sphereCamera;
            sphereRenderingEngine.cameras2D.addEdgeListener(cameraPosition => this.shiftItemsToCamera(cameraPosition));
        }
        this.updateRendererSize();
        {
            this.cameraListener = () => {
                const renderResolution = sphereRenderingEngine.resolution(new Vector2());
                if (renderResolution.y > 0) {
                    const canvasToPlanogram = renderResolution.y / this.viewableLimits.getLimitHeight();
                    const focusPoint = this.camera.planogramPoint();
                    this.lodProvider.updateCamera(focusPoint, this.camera.zoomRatio() * canvasToPlanogram);
                    const viewport = this.camera.computeViewport();
                    this.viewportListeners.forEach(it => it(viewport));
                }
            };
            this.camera.addEventListener('Updated', this.cameraListener);
        }
        this.lodProvider = new LodProvider(this.renderer, Object.assign({ cdnUrl: CDN_HOST }, pickDeviceLimits()));
    }
    shiftItemsToCamera(cameraPosition) {
        shiftObjectsCloserToCamera(this.items.reduce((list, it) => {
            if (it.object3D !== undefined)
                list.push(it.object3D);
            return list;
        }, []), cameraPosition);
    }
    makeBackgroundItem(id, imageId, backgroundImage) {
        const imageData = {
            id: imageId,
            image_name: id,
            lods_version: 0,
            naturalResolution: [backgroundImage.original_width, backgroundImage.original_height],
            thumbnails: backgroundImage.thumbnails,
            opacity: 1.0,
            fit_size: [backgroundImage.original_width, backgroundImage.original_height],
            full_size: [backgroundImage.original_width, backgroundImage.original_height]
        };
        const planogramSize = this.planogram.size();
        const backgroundItem = new SphereItem({
            id: id,
            name: 'Background',
            x: 0,
            layer: MAX_ITEM_RENDER_ORDER,
            y: -planogramSize.y,
            type: SphereItemType.Image,
            width: planogramSize.x,
            height: planogramSize.x,
            data: imageData,
            lods: [], // otherwise SphereItem constructor makes it invisible
            visible: true
        }, this.planogram, this.lodProvider);
        return backgroundItem;
    }
    addItems(itemsGroup) {
        return __awaiter(this, void 0, void 0, function* () {
            this.items = itemsGroup.items;
            this.scene.add(itemsGroup);
            this.shiftItemsToCamera(sphereRenderingEngine.cameras2D.mainCamera.position);
            const images = new Map();
            const lodData = new Map();
            itemsGroup.items.forEach(item => {
                var _a, _b;
                if (!isLodItem(item.itemData))
                    return;
                {
                    const material = this.lodProvider.getMaterial(getItemId(item));
                    item.material = material;
                    if (item.type === SphereItemType.Image || item.type === SphereItemType.Product) {
                        const imageData = item.data;
                        if (imageData.cropOffset !== undefined)
                            material.setItemUniforms({ cropOffset: { value: new Vector2(...imageData.cropOffset) } });
                        if (imageData.cropRatio !== undefined)
                            material.setItemUniforms({ cropRatio: { value: new Vector2(...imageData.cropRatio) } });
                    }
                }
                const itemData = item.itemData;
                const lodId = getLodId(item);
                const itemId = getItemId(item);
                images.set(itemId, {
                    lodId,
                    position: new PlanogramPoint(item.getViewportCenter()),
                    size: item.getSize(),
                    naturalResolution: new Vector2(...(_a = itemData.data) === null || _a === void 0 ? void 0 : _a.naturalResolution),
                    shading: packShading(itemData.data.shading, itemData.data.opacity, this.planogram.palette),
                    cornerRadius: new Vector4(...((_b = itemData.data.cornerRadius) !== null && _b !== void 0 ? _b : [0, 0, 0, 0]))
                });
                const filteredLods = skipAtlasLevels(itemData.lods).filter((lod, i) => {
                    function atlasSize(lod) {
                        var _a, _b, _c;
                        return (_c = (_b = (_a = lod.textures[0]) === null || _a === void 0 ? void 0 : _a.uv) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : 1.0;
                    }
                    if (i === 0)
                        return true;
                    if (atlasSize(lod) === 1.0)
                        return true;
                    // TODO: ask BE to filter out identical atlas LOD levels
                    return atlasSize(itemData.lods[i - 1]) !== atlasSize(lod);
                });
                lodData.set(lodId, {
                    id: parseLodId(lodId).id,
                    curator_lods: filteredLods,
                    fit_size: itemData.data.fit_size,
                    full_size: itemData.data.full_size,
                    naturalResolution: itemData.data.naturalResolution,
                    lods_version: itemData.data.lods_version
                });
            });
            const backgroundImage = this.planogram.background_images[0];
            if (backgroundImage !== undefined) {
                const BACKGROUND_ID = 'generated-background';
                const BACKGROUND_IMAGE_ID = -1;
                const backgroundItem = this.makeBackgroundItem(BACKGROUND_ID, BACKGROUND_IMAGE_ID, backgroundImage);
                this.scene.add(backgroundItem.object3D);
                makeOverflowMeshes(backgroundItem.object3D.userData.mesh);
                const lodId = createLodId(backgroundImage.id, LodSource.BACKGRDOUND);
                images.set(BACKGROUND_ID, {
                    lodId,
                    position: new PlanogramPoint(backgroundItem.getViewportCenter()),
                    size: this.planogram.size(),
                    naturalResolution: new Vector2(backgroundImage.original_width, backgroundImage.original_height),
                    shading: new Vector4(1, 1, 1, 1),
                    cornerRadius: new Vector4(0, 0, 0, 0)
                });
                lodData.set(lodId, backgroundDataToLodItem(backgroundImage));
            }
            this.lodProvider.updateImageList(images, (ids) => Promise.resolve(ids.map(id => lodData.get(id))));
            debugLog(this.scene.children);
        });
    }
    preloadLod() {
        return new Promise(resolve => {
            this.start(); // TODO: just calling this.lodProvider.update repeatedly should be enough, but it doesn't work, and this.render doesn't either
            let timeout;
            const finish = () => {
                loadingProgress.progressStage(LOADING_STAGES.TILES_FOR_LOD, 1);
                this.lodProvider.removeLoadedListener(finish);
                this.stop();
                clearTimeout(timeout);
                resolve();
            };
            timeout = setTimeout(finish, MAX_LOD_LOADING_DURATION);
            this.lodProvider.addLoadedListener(finish);
            // normally LOD updates are spread over frames to minimize FPS hit
            // during initial loading it doesn't matter, so we update as often as possible to speed up loading
        });
    }
    // scale used to mean resolution scale for kawase filter implemented by the postprocessing package
    blur(scale) {
        if (scale === undefined)
            this.canvas.style.filter = '';
        else {
            const kernelSize = (BASE_BLUR_SIZE * scale) / 0.5;
            this.canvas.style.filter = `blur(${kernelSize.toFixed(3)}px)`;
        }
    }
    start() {
        this.updateRendererSize();
        this.renderer.setAnimationLoop(() => this.render());
    }
    stop() {
        this.renderer.setAnimationLoop(null);
    }
    pauseLodUpdates(state) {
        this.lodPaused = state;
    }
    updateRendererSize() {
        const canvasSize = measureCanvasSize();
        this.camera.canvasSize = canvasSize;
        const pixelRatio = limitDevicePixelRatio(this.renderer);
        sphereRenderingEngine.canvasSize = canvasSize;
        this.renderer.setSize(canvasSize.x, canvasSize.y);
        this.renderer.setPixelRatio(pixelRatio);
    }
    resize() {
        this.updateRendererSize();
        this.renderer.setRenderTarget(null);
        this.camera.zoomTo(cameraConfig.FOV_DEFAULT);
        this.render();
    }
    onViewportChange(callback) {
        this.viewportListeners.push(callback);
    }
    render() {
        if (!this.lodPaused) {
            this.lodProvider.update();
        }
        const dt = this.clock.getDelta();
        sphereRenderingEngine.render(this.renderer);
        this.renderListeners.forEach(it => it(dt));
    }
    onRender(callback) {
        this.renderListeners.push(callback);
    }
    getInteractableObjectsAtScreenCoordinate(x, y) {
        const origin3d = this.camera.getMouseIntersection(x, y);
        const spherePoint = this.sphereShape.reverse(origin3d);
        const planogramPoint = new PlanogramPoint(this.sphereShape.planogramCoordinateViewer(spherePoint, this.planogram.size()));
        // TODO: why is offset necessary? .planogramCoordinate is off on X axis
        planogramPoint.sub({ x: 0, y: -0.25 * this.planogram.height });
        const camera2dPosition = sphereRenderingEngine.cameras2D.mainCamera.position;
        const origin2d = planogramPoint.toVectorAround(new Vector3(), camera2dPosition);
        origin2d.z = camera2dPosition.z;
        const caster2d = new Raycaster(origin2d, new Vector3(0, 0, -1));
        caster2d.layers.enableAll();
        const intersects = caster2d.intersectObjects(this.scene.children, true);
        const sortedIntersections = intersects
            .map(it => it.object)
            .sort((a, b) => {
            const groupDiff = groupOrder(b) - groupOrder(a);
            if (groupDiff !== 0)
                return groupDiff;
            const renderDiff = b.renderOrder - a.renderOrder;
            if (renderDiff !== 0)
                return renderDiff;
            else
                return b.id - a.id;
        });
        return { meshes: sortedIntersections, point: origin3d };
    }
    dispose() {
        this.lodProvider.dispose();
        this.stop();
        this.viewportListeners.splice(0, this.viewportListeners.length);
        this.renderListeners.splice(0, this.renderListeners.length);
        this.camera.removeEventListener('Updated', this.cameraListener);
        disposeObject3D(this.scene);
        sphereRenderingEngine.dispose();
    }
}
